import React, { useState } from 'react';
import {
  Button,
  ButtonEnums,
  ButtonLink,
  Container,
  Grid,
  Link,
  LinkEnums,
  NotificationBannerEnums,
  OnlyDesktop,
} from 'components/atoms';
import { User as UserTypes } from 'mxp-schemas';
import { Routes } from 'constants/index';
import {
  DescriptionForgot,
  DividerStyled,
  ImgHolder,
  LogoHeader,
  NotificationBannerStyled,
  PreFooter,
  PreviousPageStyled,
  StyledFooter,
  StyledH1,
  StyledIconError,
  StyledUl,
  WrapperForgotContent,
} from '../PageForgotEmail/PageForgotEmail';
import { InputWithValidation } from 'components/molecules';
import { Utils } from 'mxp-utils/dist/index';
import { handleEvent, PASSWORD_RESET_LINK } from 'utils/Analytics';
import { getPath } from 'utils';
import styled from 'styled-components/macro';
import { isTokenProblemError } from 'modules/user/helpers';

interface ForgotPasswordProps {
  resetPass: (email: string) => any;
  error: State.UserError | null;
  loading: boolean;
  clearPreviousResults: () => void;
}

enum SCREENS {
  RESET_START = 'RESET_START',
  SENT_SUCCESS = 'SENT_SUCCESS',
}

export const PageForgotPassword: React.FC<ForgotPasswordProps> = ({
  resetPass,
  error,
  loading,
  clearPreviousResults,
}) => {
  const [email, setEmail] = useState('');
  const [isForgotPassSent, setIsForgotPassSent] = useState(false);

  const isEmailValid: boolean = Utils.isEmailFormatValid(email);
  const isEmailEmpty = !Boolean(email);
  const [sendEmailButtonDisabled, setSendEmailButtonDisabled] = useState(false);
  const isEmailValidOrEmpty: boolean = isEmailValid || isEmailEmpty;
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };
  const handleSendResetPass = React.useCallback(() => {
    resetPass(email);
    handleEvent(
      {
        text: 'Reset password',
        href: getPath(Routes.FORGOT_PASSWORD),
      },
      PASSWORD_RESET_LINK
    );
    setIsForgotPassSent(true);
  }, [resetPass, email, setIsForgotPassSent]);

  const handleSendAgain = React.useCallback(() => {
    if (email) {
      resetPass(email);
    }
  }, [resetPass, email]);

  const currentScreen = getCurrentScreen(isForgotPassSent);

  React.useEffect(() => {
    if (!isTokenProblemError(error)) {
      clearPreviousResults();
    }
    return function cleanup() {
      clearPreviousResults();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const hasError = Boolean(error?.errorCode === UserTypes.UserErrorCodes.LOGIN_NOT_FOUND);
    const loginDisabled = !isEmailValid || isEmailEmpty || hasError;
    setSendEmailButtonDisabled(loginDisabled);
  }, [setSendEmailButtonDisabled, isEmailValid, isEmailEmpty, error]);

  React.useEffect(() => {
    const hasLoginNotFoundError = Boolean(error?.errorCode === UserTypes.UserErrorCodes.LOGIN_NOT_FOUND);
    if (hasLoginNotFoundError) {
      // clear email field
      setEmail('');
    }
  }, [error]);

  // unlock login button on loginForm changes
  React.useEffect(() => {
    const loginDisabled = !isEmailValid || isEmailEmpty;
    setSendEmailButtonDisabled(loginDisabled);
  }, [setSendEmailButtonDisabled, isEmailValid, isEmailEmpty, email]);

  const getCtaButton = () => {
    switch (currentScreen as SCREENS) {
      case SCREENS.RESET_START:
        return (
          <StyledButtonSend
            size={ButtonEnums.sizes.medium}
            loading={loading}
            onClick={handleSendResetPass}
            testId="get-email"
            variant={ButtonEnums.variants.primary}
            disabled={sendEmailButtonDisabled}
          >
            Send reset password email
          </StyledButtonSend>
        );
      case SCREENS.SENT_SUCCESS:
        return (
          <FlexWrap>
            <StyledButtonLinkSibling
              size={ButtonEnums.sizes.medium}
              to={getPath(Routes.LOGIN)}
              testId="go-to-login"
              variant={ButtonEnums.variants.primary}
            >
              Go to log in
            </StyledButtonLinkSibling>
            <StyledButtonSibling
              size={ButtonEnums.sizes.medium}
              onClick={handleSendAgain}
              testId="send-again"
              variant={ButtonEnums.variants.secondary}
            >
              Send again
            </StyledButtonSibling>
          </FlexWrap>
        );
    }
  };

  const getTitle = () => {
    switch (currentScreen as SCREENS) {
      case SCREENS.RESET_START:
        return 'Forgot your password?';
      case SCREENS.SENT_SUCCESS:
        return 'Your reset email is on its way.';
    }
  };
  const getDescription = () => {
    switch (currentScreen as SCREENS) {
      case SCREENS.RESET_START:
        return 'Enter your email address below. We’ll send you a link to change your password.';
      case SCREENS.SENT_SUCCESS:
        return (
          <>
            <p>
              <strong>
                If the information provided matches our records, you will receive an email to reset your password.
              </strong>
            </p>
            <p>
              <b>What’s next?</b>&nbsp;The email may take a few moments to arrive. Click the link in your email to reset
              your password. The link expires in 48 hours, so be sure to use it soon. Please check your junk email or
              spam folder if you did not receive the email.
            </p>
          </>
        );
    }
  };

  return (
    <>
      <div>
        <LogoHeader />
        <Container>
          <Grid>
            <Grid.Row>
              <Grid.Column computer={8}>
                <WrapperForgotContent>
                  {!isTokenProblemError(error) ? (
                    <PreviousPageStyled backText="Back to previous page" previousRoute={getPath(Routes.LOGIN)} />
                  ) : (
                    <OnlyDesktop>
                      <StyledSpacer />
                    </OnlyDesktop>
                  )}
                  <StyledH1 data-testid="page-title">{getTitle()}</StyledH1>
                  <DividerStyled />
                  <DescriptionForgot>{getDescription()}</DescriptionForgot>
                  <WrapContent>
                    <>
                      {currentScreen === SCREENS.SENT_SUCCESS && <FormMessages error={error} />}
                      {currentScreen === SCREENS.RESET_START && (
                        <InputWithValidation
                          onChange={handleEmailChange}
                          name="email"
                          testId="email"
                          isCorrect={isEmailValidOrEmpty}
                          labelId="email"
                          labelName="Email address"
                          value={email}
                          autoFocus
                          errorMessage="Please enter a valid email address"
                          type="email"
                        />
                      )}
                      {getCtaButton()}
                    </>
                  </WrapContent>
                </WrapperForgotContent>
              </Grid.Column>
              <Grid.Column computer={8}>
                <ImgHolder data-testid="forgot-pass-email-img" />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Container>
      </div>
      <PreFooter />
      <StyledFooter />
    </>
  );
};

const getCurrentScreen = (isForgotPassSent: boolean): SCREENS => {
  if (isForgotPassSent) {
    return SCREENS.SENT_SUCCESS;
  }
  return SCREENS.RESET_START;
};

interface FormMessagesProps {
  error: any;
}
const FormMessages: React.FC<FormMessagesProps> = ({ error }) => {
  return (
    <>
      <NotificationBannerStyled
        testId="error-incorrect-id-banner"
        childrenTestId="error-incorrect-id-children"
        variant={NotificationBannerEnums.variant.red}
        icon={<StyledIconError />}
      >
        <StyledUl>
          <li>
            If you aren’t sure which email we have on file,&nbsp;
            <Link type={LinkEnums.type.inlineLink} to={getPath(Routes.FORGOT_EMAIL)} testId="forgot-email">
              look up your email address
            </Link>
            &nbsp; using your account number.
          </li>
          <li>
            New to AICPA or CIMA?
            <br />
            <Link type={LinkEnums.type.inlineLink} to={getPath(Routes.REGISTRATION)} testId="go-to-register">
              Create a free account
            </Link>
            <br />
            <b>Note: </b> If you have an existing account, do not create a new one. Using your existing account will
            ensure you have access to all of your memberships, discounts or previous purchases.
          </li>
          <li>
            Contact Us: <br />
            For AICPA &nbsp;
            <Link type={LinkEnums.type.inlineLink} to={getPath(Routes.CONTACT_US_AICPA)} testId="contact-us-aicpa">
              Contact Us - AICPA
            </Link>
            <br />
            For CIMA &nbsp;
            <Link type={LinkEnums.type.inlineLink} to={getPath(Routes.CONTACT_US_CIMA)} testId="contact-us-cima">
              Contact Us - CIMA
            </Link>
          </li>
        </StyledUl>
      </NotificationBannerStyled>

      {isTokenProblemError(error) && (
        <NotificationBannerStyled
          testId="error-incorrect-id-banner"
          childrenTestId="error-incorrect-id-children"
          variant={NotificationBannerEnums.variant.red}
          icon={<StyledIconError />}
        >
          Sorry. For security reasons, this password-reset link is invalid. Password-reset links expire after 48 hours.
          Please enter your email address below to try again.
        </NotificationBannerStyled>
      )}
    </>
  );
};

const FlexWrap = styled.div`
  display: flex;
  ${props => props.theme.mediaQueries.mobileOnly} {
    display: block;
  }
`;

const StyledSpacer = styled.div`
  margin-top: ${props => props.theme.pxToRem(80)};
`;

const WrapContent = styled.div`
  max-width: ${props => props.theme.pxToRem(360)};
`;

const StyledButton = styled(Button)`
  &&&& {
    padding: ${props => props.theme.pxToRem(8)} ${props => props.theme.pxToRem(16)};
  }
`;

const StyledButtonSend = styled(StyledButton)`
  &&&& {
    margin-top: ${props => props.theme.pxToRem(18)};
  }
`;

const StyledButtonLink = styled(ButtonLink)`
  &&&& {
    margin-right: ${props => props.theme.pxToRem(20)};
    padding: ${props => props.theme.pxToRem(8)} ${props => props.theme.pxToRem(16)};
  }
`;

const StyledButtonSibling = styled(StyledButton)`
  &&& {
    ${props => props.theme.mediaQueries.mobileOnly} {
      width: 100%;
    }
  }
`;

const StyledButtonLinkSibling = styled(StyledButtonLink)`
  &&&&& {
    ${props => props.theme.mediaQueries.mobileOnly} {
      width: 100%;
      margin-bottom: ${props => props.theme.pxToRem(10)};
    }
  }
`;
